import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core'
import { Clear, DragIndicator } from '@material-ui/icons'
import { FlexHorizontal, IBoard, MessageCard, Spacer } from '@vestaboard/installables'
import React, { Fragment, useEffect, useState } from 'react'
import { SearchMessageSortBy, SearchMessageSortDirection } from '../types'
import { SortDirection, SortableTableCell } from '../../../ui/SortableTableCell'

import { Alert } from '@material-ui/lab'
import InfiniteScroll from 'react-infinite-scroller'
import { MessageModal } from '../../message-v2'
import { SearchInput } from '../../../ui/SearchInput'
import { SearchMessageType } from '../../message-v2/types'
import { TruncatedId } from '../../truncated-id'
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { ListPicksV2_listSearchMessages_searchMessages_PickV2 } from '../hooks/__generated__/ListPicksV2'
import { useHistory } from 'react-router'
import { useListPicks } from '../hooks/useListPicks'
import { useUpdatePick } from '../hooks/useUpdatePick'

const PER_PAGE = 25

enum PickTab {
  Upcoming = 'upcoming',
  History = 'history'
}

const SortablePickRow = ({
  pick,
  onSetMessageId,
  onSetCategoryTitle
}: {
  pick: ListPicksV2_listSearchMessages_searchMessages_PickV2
  onSetMessageId: (id: string) => void
  onSetCategoryTitle: (title: string) => void
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: pick.id })

  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell style={{ width: 40 }}>
        <span style={{ cursor: 'grab', display: 'flex' }} {...attributes} {...listeners}>
          <DragIndicator fontSize='small' color='disabled' />
        </span>
      </TableCell>
      <TableCell>
        <TruncatedId value={pick.id} full path={`/picks/${pick.id}`} />
      </TableCell>
      <TableCell>{pick.date}</TableCell>
      <TableCell>{pick.message.cardSize === '15x3' ? 'Note' : 'Flagship'}</TableCell>
      <TableCell>
        <FlexHorizontal>
          {(pick?.categories || []).map((category, index) => (
            <Fragment key={`${pick.id}-${category.id}`}>
              {index ? <Box width={7} /> : null}
              <Button size='small' variant='outlined' onClick={() => onSetCategoryTitle(category.title)}>
                {category.title}
              </Button>
            </Fragment>
          ))}
        </FlexHorizontal>
      </TableCell>
      <TableCell>
        {(pick?.message?.tags || []).map((tag, index) => (
          <Fragment key={`${pick.id}-${tag}`}>
            {index ? <Box height={3} /> : null}
            <Chip label={tag} />
          </Fragment>
        ))}
      </TableCell>
      <TableCell>
        <MessageCard
          boardStyle={pick.message.cardSize === '15x3' ? 'note-black' : 'black'}
          characters={pick.message.characters as IBoard}
          onEditPress={() => onSetMessageId(pick.message.id)}
        />
      </TableCell>
    </TableRow>
  )
}

export const ListPicks = () => {
  const [messageId, setMessageId] = useState<string | null>(null)
  const history = useHistory()
  const [searchTerm, setSearchTerm] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<SearchMessageSortDirection>(SearchMessageSortDirection.desc)
  const [sortBy, setSortBy] = useState<SearchMessageSortBy>(SearchMessageSortBy.date)
  const [categoryTitle, setCategoryTitle] = useState<string | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [activeTab, setActiveTab] = useState<PickTab>(PickTab.Upcoming)
  const [cardSizeFilter, setCardSizeFilter] = useState<string>('all')
  const [dateFilter, setDateFilter] = useState<string | null>(null)
  const [manualOrder, setManualOrder] = useState<string[] | null>(null)
  const [updatePick] = useUpdatePick()
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const todayString = new Date().toISOString().split('T')[0]

  const isSearchTermDate = searchTerm && !!/^\d{4}-\d{2}-\d{2}$/.test(searchTerm)

  console.log('dateString', dateFilter)

  const input = {
    cursor: null,
    limit: PER_PAGE,
    sortBy,
    sortDirection,
    term: !isSearchTermDate ? searchTerm || undefined : undefined,
    dateString: dateFilter || undefined,
    date: isSearchTermDate ? new Date(searchTerm).getTime() : undefined,
    types: [SearchMessageType.Pick],
    tags: categoryTitle ? [categoryTitle] : undefined,
    showHidden: true
  }

  const { loading, error, data, fetchMore } = useListPicks({
    input
  })

  const filteredPicks = (data?.listSearchMessages.searchMessages || [])
    .filter(pick => {
      if (pick.__typename !== 'PickV2') return false
      if (activeTab === PickTab.Upcoming && pick.date <= todayString) return false
      if (activeTab === PickTab.History && pick.date > todayString) return false
      if (cardSizeFilter !== 'all' && pick.message.cardSize !== cardSizeFilter) return false
      if (dateFilter && pick.date !== dateFilter) return false
      return true
    })
    .filter((pick, index, arr) => {
      if (pick.__typename !== 'PickV2') return true
      return arr.findIndex(p => p.__typename === 'PickV2' && p.id === pick.id) === index
    })
    .sort((a, b) => {
      const aDate = a.__typename === 'PickV2' ? a.date : ''
      const bDate = b.__typename === 'PickV2' ? b.date : ''
      if (aDate > bDate) return -1
      if (aDate < bDate) return 1
      const aOrder = a.__typename === 'PickV2' ? a.sortOrder : 0
      const bOrder = b.__typename === 'PickV2' ? b.sortOrder : 0
      return aOrder - bOrder
    })

  useEffect(() => {
    setManualOrder(null)
  }, [dateFilter])

  const picksToRender =
    dateFilter && manualOrder
      ? [...filteredPicks].sort((a, b) => {
          const aId = a.__typename === 'PickV2' ? a.id : ''
          const bId = b.__typename === 'PickV2' ? b.id : ''
          return manualOrder.indexOf(aId) - manualOrder.indexOf(bId)
        })
      : filteredPicks

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const picks = picksToRender.filter(
      (p): p is ListPicksV2_listSearchMessages_searchMessages_PickV2 => p.__typename === 'PickV2'
    )
    const ids = picks.map(p => p.id)
    const newIds = arrayMove(ids, ids.indexOf(active.id as string), ids.indexOf(over.id as string))
    setManualOrder(newIds)
    newIds.forEach((id, index) => {
      const pick = picks.find(p => p.id === id)
      if (!pick) return
      updatePick({ variables: { input: { id, date: pick.date, messageId: pick.message.id, order: index } } })
    })
  }

  return (
    <>
      <MessageModal
        messageId={messageId}
        onClose={() => {
          setMessageId(null)
        }}
      />
      <Box>
        <FlexHorizontal spaceBetween>
          <Typography variant='h4'>Picks</Typography>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            {categoryTitle ? (
              <>
                <Button
                  onClick={() => {
                    setCategoryTitle(null)
                  }}
                  variant='outlined'
                  color='default'
                  endIcon={<Clear />}>
                  {categoryTitle}
                </Button>
                <Box width={20} />
              </>
            ) : null}
            <TextField
              label='Date'
              type='date'
              size='small'
              variant='outlined'
              value={dateFilter || ''}
              onChange={e => {
                const val = e.target.value || null
                setDateFilter(val)
                if (val) {
                  setActiveTab(val > todayString ? PickTab.Upcoming : PickTab.History)
                }
              }}
              style={{ width: 180 }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: dateFilter ? (
                  <InputAdornment position='end'>
                    <IconButton size='small' onClick={() => setDateFilter(null)}>
                      <Clear fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }}
            />
            <Box width={20} />
            <FormControl variant='outlined' size='small' style={{ width: 160 }}>
              <InputLabel>Card Size</InputLabel>
              <Select
                value={cardSizeFilter}
                onChange={e => setCardSizeFilter(e.target.value as string)}
                label='Card Size'>
                <MenuItem value='all'>All</MenuItem>
                <MenuItem value='15x3'>Note</MenuItem>
                <MenuItem value='22x6'>Flagship</MenuItem>
              </Select>
            </FormControl>
            <Box width={20} />
            <SearchInput onSearch={setSearchTerm} size='small' />
            <Box width={20} />
            <Button
              onClick={() => history.push('/picks/create', { date: dateFilter })}
              variant='contained'
              color='primary'>
              Create New Pick
            </Button>
          </div>
        </FlexHorizontal>
        <Spacer size='large' />
        <Tabs
          value={activeTab}
          onChange={(_e, val) => {
            setActiveTab(val)
            setDateFilter(null)
          }}>
          <Tab label='Upcoming' value={PickTab.Upcoming} />
          <Tab label='History' value={PickTab.History} />
        </Tabs>
        <Spacer size='medium' />
        {loading && !data?.listSearchMessages.searchMessages ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity='error'>There was an error loading the picks</Alert>
        ) : !filteredPicks.length ? (
          <Alert severity='info'>There are no picks</Alert>
        ) : (
          <>
            <InfiniteScroll
              pageStart={0}
              loadMore={async () => {
                try {
                  if (isLoadingMore || !fetchMore) {
                    return
                  }

                  setIsLoadingMore(true)
                  return await fetchMore({
                    updateQuery(previousQueryResult, options) {
                      const newSearch = options.fetchMoreResult?.listSearchMessages.searchMessages || []
                      const previousSearch = previousQueryResult.listSearchMessages.searchMessages || []

                      const seen = new Set<string>()
                      const deduped = [...previousSearch, ...newSearch].filter(item => {
                        if (item.__typename !== 'PickV2') return true
                        if (seen.has(item.id)) return false
                        seen.add(item.id)
                        return true
                      })

                      return {
                        listSearchMessages: {
                          ...previousQueryResult.listSearchMessages,
                          nextCursor: options.fetchMoreResult?.listSearchMessages.nextCursor || null,
                          searchMessages: deduped
                        }
                      }
                    },
                    variables: {
                      input: {
                        ...input,
                        cursor: data?.listSearchMessages.nextCursor || null
                      }
                    }
                  }).then(res => {
                    setIsLoadingMore(false)
                    return res
                  })
                } catch (err) {}
              }}
              hasMore={
                !dateFilter &&
                !!data?.listSearchMessages?.nextCursor &&
                !(
                  activeTab === PickTab.Upcoming &&
                  (data?.listSearchMessages.searchMessages || []).some(
                    pick => pick.__typename === 'PickV2' && pick.date >= todayString
                  )
                )
              }
              loader={
                <Box>
                  <Spacer size='large' />
                  <CircularProgress />
                </Box>
              }>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {dateFilter ? <TableCell style={{ width: 40 }} /> : null}
                      <TableCell style={{ width: 360 }}>ID</TableCell>
                      <SortableTableCell
                        direction={SortDirection[sortDirection]}
                        active={sortBy === SearchMessageSortBy.date}
                        width={120}
                        onClick={direction => {
                          setSortDirection(SearchMessageSortDirection[direction])
                          setSortBy(SearchMessageSortBy.date)
                        }}>
                        Publish Date
                      </SortableTableCell>
                      <TableCell style={{ width: 100 }}>Format</TableCell>
                      <TableCell style={{ width: 300 }}>Categories</TableCell>
                      <TableCell style={{ maxWidth: 300 }}>Tags</TableCell>
                      <SortableTableCell
                        direction={SortDirection[sortDirection]}
                        active={sortBy === SearchMessageSortBy.messageText}
                        onClick={direction => {
                          setSortDirection(SearchMessageSortDirection[direction])
                          setSortBy(SearchMessageSortBy.messageText)
                        }}>
                        Message
                      </SortableTableCell>
                    </TableRow>
                  </TableHead>
                  {dateFilter ? (
                    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                      <SortableContext
                        items={picksToRender.filter(p => p.__typename === 'PickV2').map(p => p.id)}
                        strategy={verticalListSortingStrategy}>
                        <TableBody>
                          {picksToRender.map(pick =>
                            pick.__typename === 'PickV2' ? (
                              <SortablePickRow
                                key={pick.id}
                                pick={pick}
                                onSetMessageId={setMessageId}
                                onSetCategoryTitle={setCategoryTitle}
                              />
                            ) : null
                          )}
                        </TableBody>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <TableBody>
                      {filteredPicks.map(pick =>
                        pick.__typename === 'PickV2' ? (
                          <TableRow key={pick.id}>
                            <TableCell>
                              <TruncatedId value={pick.id} full path={`/picks/${pick.id}`} />
                            </TableCell>
                            <TableCell>{pick.date}</TableCell>
                            <TableCell>{pick.message.cardSize === '15x3' ? 'Note' : 'Flagship'}</TableCell>
                            <TableCell>
                              <FlexHorizontal>
                                {(pick?.categories || []).map((category, index) => (
                                  <Fragment key={`${pick.id}-${category.id}`}>
                                    {index ? <Box width={7} /> : null}
                                    <Button
                                      size='small'
                                      variant='outlined'
                                      onClick={() => {
                                        setCategoryTitle(category.title)
                                      }}>
                                      {category.title}
                                    </Button>
                                  </Fragment>
                                ))}
                              </FlexHorizontal>
                            </TableCell>
                            <TableCell>
                              {(pick?.message?.tags || []).map((tag, index) => (
                                <Fragment key={`${pick.id}-${tag}`}>
                                  {index ? <Box height={3} /> : null}
                                  <Chip label={tag} />
                                </Fragment>
                              ))}
                            </TableCell>
                            <TableCell>
                              <MessageCard
                                boardStyle={pick.message.cardSize === '15x3' ? 'note-black' : 'black'}
                                characters={pick.message.characters as IBoard}
                                onEditPress={() => {
                                  setMessageId(pick.message.id)
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ) : null
                      )}
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </InfiniteScroll>
          </>
        )}
      </Box>
    </>
  )
}
